import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, MenuItem, Select, Typography, CircularProgress } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import apiService from '../../services/apiService';
import { formatDateForInput } from '../../utils/dateUtils';

interface Account {
  id: string;
  name: string;
  code: string;
}

interface PaymentVoucherFormProps {
  visible: boolean;
  mode: 'view' | 'edit' | 'create';
  initialValues: any;
  onSubmit: (values: any) => void;
  onCancel: () => void;
}

const PaymentVoucherForm: React.FC<PaymentVoucherFormProps> = ({
  visible,
  mode,
  initialValues,
  onSubmit,
  onCancel
}) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Load accounts on component mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await apiService.get<Account[]>('/api/accounts');
        setAccounts(response);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };

    fetchAccounts();
  }, []);

  // Set up form validation schema
  const validationSchema = Yup.object({
    debitAccount: Yup.string().required('الحساب المدين مطلوب'),
    creditAccount: Yup.string().required('الحساب الدائن مطلوب'),
    amount: Yup.number()
      .required('المبلغ مطلوب')
      .positive('يجب أن يكون المبلغ موجبًا'),
    description: Yup.string().required('الوصف مطلوب'),
    date: Yup.date().required('التاريخ مطلوب'),
    beneficiary: Yup.string().required('المستفيد مطلوب')
  });

  // Initialize form with Formik
  const formik = useFormik({
    initialValues: {
      debitAccount: initialValues?.debitAccount?.id || '',
      creditAccount: initialValues?.creditAccount?.id || '',
      amount: initialValues?.amount || '',
      description: initialValues?.description || '',
      date: initialValues ? formatDateForInput(initialValues.date) : formatDateForInput(new Date()),
      beneficiary: initialValues?.beneficiary || '',
      paymentMethod: initialValues?.paymentMethod || 'نقدي'
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await onSubmit(values);
      } finally {
        setLoading(false);
      }
    }
  });

  const isViewMode = mode === 'view';
  const dialogTitle = 
    mode === 'create' ? 'إنشاء سند صرف جديد' : 
    mode === 'edit' ? 'تعديل سند صرف' : 
    'عرض تفاصيل سند الصرف';

  return (
    <Dialog open={visible} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          {isViewMode && initialValues?.voucherNumber && (
            <Typography variant="subtitle1" gutterBottom>
              رقم السند: {initialValues.voucherNumber}
            </Typography>
          )}

          <FormControl fullWidth margin="normal" disabled={isViewMode}>
            <InputLabel id="debit-account-label">الحساب المدين</InputLabel>
            <Select
              labelId="debit-account-label"
              id="debitAccount"
              name="debitAccount"
              value={formik.values.debitAccount}
              onChange={formik.handleChange}
              error={formik.touched.debitAccount && Boolean(formik.errors.debitAccount)}
              label="الحساب المدين"
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name} ({account.code})
                </MenuItem>
              ))}
            </Select>
            {formik.touched.debitAccount && formik.errors.debitAccount && (
              <Typography color="error" variant="caption">
                {String(formik.errors.debitAccount)}
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth margin="normal" disabled={isViewMode}>
            <InputLabel id="credit-account-label">الحساب الدائن</InputLabel>
            <Select
              labelId="credit-account-label"
              id="creditAccount"
              name="creditAccount"
              value={formik.values.creditAccount}
              onChange={formik.handleChange}
              error={formik.touched.creditAccount && Boolean(formik.errors.creditAccount)}
              label="الحساب الدائن"
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name} ({account.code})
                </MenuItem>
              ))}
            </Select>
            {formik.touched.creditAccount && formik.errors.creditAccount && (
              <Typography color="error" variant="caption">
                {String(formik.errors.creditAccount)}
              </Typography>
            )}
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            id="beneficiary"
            name="beneficiary"
            label="المستفيد"
            value={formik.values.beneficiary}
            onChange={formik.handleChange}
            error={formik.touched.beneficiary && Boolean(formik.errors.beneficiary)}
            helperText={formik.touched.beneficiary && String(formik.errors.beneficiary)}
            disabled={isViewMode}
          />

          <TextField
            fullWidth
            margin="normal"
            id="amount"
            name="amount"
            label="المبلغ"
            type="number"
            value={formik.values.amount}
            onChange={formik.handleChange}
            error={formik.touched.amount && Boolean(formik.errors.amount)}
            helperText={formik.touched.amount && String(formik.errors.amount)}
            disabled={isViewMode}
            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
          />

          <FormControl fullWidth margin="normal" disabled={isViewMode}>
            <InputLabel id="payment-method-label">طريقة الدفع</InputLabel>
            <Select
              labelId="payment-method-label"
              id="paymentMethod"
              name="paymentMethod"
              value={formik.values.paymentMethod}
              onChange={formik.handleChange}
              label="طريقة الدفع"
            >
              <MenuItem value="نقدي">نقدي</MenuItem>
              <MenuItem value="شيك">شيك</MenuItem>
              <MenuItem value="تحويل بنكي">تحويل بنكي</MenuItem>
              <MenuItem value="بطاقة ائتمان">بطاقة ائتمان</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            id="description"
            name="description"
            label="الوصف"
            value={formik.values.description}
            onChange={formik.handleChange}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && String(formik.errors.description)}
            disabled={isViewMode}
            multiline
            rows={2}
          />

          <TextField
            fullWidth
            margin="normal"
            id="date"
            name="date"
            label="التاريخ"
            type="date"
            value={formik.values.date}
            onChange={formik.handleChange}
            error={formik.touched.date && Boolean(formik.errors.date)}
            helperText={formik.touched.date && String(formik.errors.date)}
            disabled={isViewMode}
            InputLabelProps={{ shrink: true }}
          />

          {isViewMode && initialValues?.status && (
            <Typography variant="subtitle1" gutterBottom>
              الحالة: {initialValues.status}
            </Typography>
          )}

          {isViewMode && initialValues?.createdBy && (
            <Typography variant="subtitle1" gutterBottom>
              تم الإنشاء بواسطة: {initialValues.createdBy}
            </Typography>
          )}

          {isViewMode && initialValues?.createdAt && (
            <Typography variant="subtitle1" gutterBottom>
              تاريخ الإنشاء: {formatDateForInput(initialValues.createdAt)}
            </Typography>
          )}

          {isViewMode && initialValues?.approvedBy && (
            <Typography variant="subtitle1" gutterBottom>
              تمت الموافقة بواسطة: {initialValues.approvedBy}
            </Typography>
          )}

          {isViewMode && initialValues?.approvedAt && (
            <Typography variant="subtitle1" gutterBottom>
              تاريخ الموافقة: {formatDateForInput(initialValues.approvedAt)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} color="inherit">
            {isViewMode ? 'إغلاق' : 'إلغاء'}
          </Button>
          {!isViewMode && (
            <Button type="submit" color="primary" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'حفظ'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PaymentVoucherForm;